/**
 * TiqetsExperienceContent.js
 *
 * Content section for Tiqets experience cards in the social feed and search.
 * Mirrors ViatorExperienceContent (affiliate, city-scoped, external link) but
 * adds Option-B schedule hints: a "Recurring shows" badge for show/concert
 * sub-categories and time-of-day / seasonal chips derived from Tiqets tags.
 *
 * Tiqets data does not include specific event_date/event_time — products are
 * recurring series, the date is picked at booking on Tiqets. The card sets
 * expectations honestly via these chips.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../shared/Icon';
import { formatCurrency } from '../../../utils/currencyFormat';

const StarRating = ({ rating, reviewCount }) => {
  const { t } = useTranslation('feedCard');
  if (!rating) return null;
  return (
    <span
      className="flex items-center gap-1"
      title={t('tiqetsExperienceContent.starRating.title')}
    >
      <Icon name="starFilled" className="w-3.5 h-3.5 text-accent" />
      <span className="font-medium text-content-primary">{Number(rating).toFixed(1)}</span>
      {!!reviewCount && (
        <span className="text-content-muted">({reviewCount.toLocaleString()})</span>
      )}
    </span>
  );
};

const TiqetsExperienceContent = ({ data }) => {
  const { t } = useTranslation('feedCard');

  const formatPrice = (price, currency, freeAdmission) => {
    if (!price) return null;
    // Free-entry venues (UK national museums etc.): the floor price is an
    // add-on (audio guide / tour), not admission — label it so.
    return t(freeAdmission ? 'tiqetsExperienceContent.priceFreeEntryFrom' : 'tiqetsExperienceContent.priceFrom', { price: formatCurrency(price, currency) });
  };

  const formatDuration = (minutes, text) => {
    if (text) return text;
    if (!minutes) return null;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0
        ? t('tiqetsExperienceContent.duration.hoursMinutes', { hours, mins })
        : t('tiqetsExperienceContent.duration.hoursOnly', { hours });
    }
    return t('tiqetsExperienceContent.duration.minutesOnly', { minutes });
  };

  const { title, description, location, typeData } = data;
  const {
    rating,
    reviewCount = 0,
    priceFrom,
    freeAdmission,
    priceCurrency,
    durationMinutes,
    durationText,
    primaryCategory,
    isRecurringShow,
    scheduleChips = [],
    city,
    country,
    address
  } = typeData || {};

  const priceDisplay = formatPrice(priceFrom, priceCurrency, freeAdmission);
  const durationDisplay = formatDuration(durationMinutes, durationText);
  const locationDisplay = location || address || (city && country ? `${city}, ${country}` : city);

  return (
    <div className="flex-grow">
      <h3 className="font-semibold text-content-primary mb-2 line-clamp-1">
        <Icon name="ticket" className="w-4 h-4 inline mr-1" />{title}
      </h3>

      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-content-secondary mb-2">
        <StarRating rating={rating} reviewCount={reviewCount} />
        {durationDisplay && (
          <span className="flex items-center">
            <Icon name="clock" className="w-3.5 h-3.5 mr-1" />
            {durationDisplay}
          </span>
        )}
        {priceDisplay && (
          <span className="font-medium text-content-primary">{priceDisplay}</span>
        )}
      </div>

      {/* Badges row — category, schedule hints (Option B), recurring marker */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {primaryCategory && (
          <span className="px-2 py-0.5 bg-warning-light text-warning-dark text-xs rounded-full">
            {primaryCategory}
          </span>
        )}
        {isRecurringShow && (
          <span
            className="px-2 py-0.5 bg-info-light text-info-dark text-xs rounded-full"
            title={t('tiqetsExperienceContent.recurringBadge.title')}
          >
            {t('tiqetsExperienceContent.recurringBadge.label')}
          </span>
        )}
        {scheduleChips.map((chip, i) => (
          <span
            key={i}
            className="px-2 py-0.5 bg-surface-secondary text-content-secondary text-xs rounded-full inline-flex items-center gap-1"
            title={chip.title || chip.label}
          >
            {chip.icon && <span aria-hidden="true">{chip.icon}</span>}
            {chip.label}
          </span>
        ))}
      </div>

      {/* Location */}
      {locationDisplay && (
        <p className="text-sm text-content-secondary mb-2 line-clamp-1">
          <Icon name="mapPin" className="w-3.5 h-3.5 inline mr-1" />
          {locationDisplay}
        </p>
      )}

      {/* Description */}
      {description && (
        <p className="text-sm text-content-secondary mb-3 line-clamp-2">
          {description}
        </p>
      )}
    </div>
  );
};

export default memo(TiqetsExperienceContent);
